import { createFileRoute } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '@/lib/api';
import { toast } from '@/template/lib/toast';
import { OperatorUserStats } from '@/components/features/operator/operator-user-stats';
import { OperatorStatsCards } from '@/components/features/operator/OperatorStatsCards';
import { OperatorAlertsSection } from '@/components/features/operator/OperatorAlertsSection';
import { PendingApprovalsTable } from '@/components/features/operator/PendingApprovalsTable';
import type { Id } from 'convex/_generated/dataModel';
import { useTranslation } from 'react-i18next';
import { formatBerlinDateWithOptions } from '@/lib/berlin-time';

// Heavy viz (recharts + react-leaflet) is split out of the dashboard chunk and loaded on demand.
const WeeklyActivityChart = lazy(() => import('@/components/features/operator/WeeklyActivityChart').then((m) => ({ default: m.WeeklyActivityChart })));
const RegionalDistributionChart = lazy(() => import('@/components/features/operator/RegionalDistributionChart').then((m) => ({ default: m.RegionalDistributionChart })));
const ServiceMap = lazy(() => import('@/components/features/operator/ServiceMap').then((m) => ({ default: m.ServiceMap })));

const VizFallback = ({ className = 'h-80' }: { className?: string }) => <div className={`${className} rounded-lg border border-border bg-card animate-pulse`} role='status' aria-label='Wird geladen' />;

const OperatorDashboard = () => {
  const { t, i18n } = useTranslation('operator');

  const formatDate = (date: Date): string => {
    return formatBerlinDateWithOptions(date.getTime(), {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }, i18n.language || 'de-DE');
  };

  // Fetch dashboard data
  const actionCounts = useQuery(api.app.operatorDashboard.getOperatorActionCounts);
  const stats = useQuery(api.app.operatorDashboard.getOperatorDashboardStats);
  const alerts = useQuery(api.app.operatorDashboard.getOperatorAlerts);
  const weeklyActivity = useQuery(api.app.operatorDashboard.getWeeklyActivity);
  const regionalDistribution = useQuery(api.app.operatorDashboard.getRegionalDistribution);
  const serviceLocations = useQuery(api.app.operatorDashboard.getActiveServiceLocations);

  // Fetch pending verifications (limit 6 for dashboard preview)
  const pendingVerificationsData = useQuery(api.app.verificationOperator.listPendingVerificationKeysetPaginated, {
    status: 'pending',
    pageSize: 6,
    includeTotal: false,
  });
  const pendingVerificationsCount = useQuery(api.app.verificationOperator.countSubstitutesByVerificationStatus, {
    status: 'pending',
  });

  // Mutations for approve/reject actions
  const approveMutation = useMutation(api.app.verificationOperator.approveVerification);
  const rejectMutation = useMutation(api.app.verificationOperator.requestVerificationChanges);

  const handleApprove = async (id: Id<'userProfiles'>): Promise<void> => {
    try {
      await approveMutation({
        userId: id as unknown as Id<'users'>,
        notes: 'Approved from operator dashboard',
      });
      toast.success(t('dashboard.toasts.userApproved'));
    } catch (error) {
      console.error('Approval error:', error);
      toast.error(t('dashboard.toasts.userApproveError'));
    }
  };

  const handleReject = async (id: Id<'userProfiles'>): Promise<void> => {
    try {
      await rejectMutation({
        userId: id as unknown as Id<'users'>,
        documentType: 'both',
        notes: 'Weitere Informationen erforderlich. Bitte aktualisieren Sie Ihre Dokumente.',
      });
      toast.success(t('dashboard.toasts.changesRequested'));
    } catch (error) {
      console.error('Rejection error:', error);
      toast.error(t('dashboard.toasts.changesRequestError'));
    }
  };

  const transformedApprovals = (pendingVerificationsData?.page || []).map((item) => {
    const fullName = [item.firstName, item.lastName].filter(Boolean).join(' ') || t('dashboard.fallbacks.unknown');
    const specialty = item.specialization.length > 0 ? item.specialization[0] : t('dashboard.fallbacks.noSpecialization');

    return {
      id: item.userId as unknown as Id<'userProfiles'>,
      name: fullName,
      email: item.email,
      role: 'substitute' as const,
      specialty,
      city: 'N/A',
    };
  });

  const isLoading = actionCounts === undefined || stats === undefined || alerts === undefined;

  if (isLoading) {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <div className='flex flex-col items-center gap-4'>
          <div className='relative'>
            <div className='w-12 h-12 border-2 border-primary/20 rounded-full' />
            <div className='absolute inset-0 w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin' />
          </div>
          <p className='text-sm text-muted-foreground'>{t('dashboard.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className='p-4 sm:p-6 lg:p-8 space-y-6'>
      {/* Page Header */}
      <div>
        <h1 className='text-2xl font-semibold text-foreground'>{t('dashboard.title')}</h1>
        <p className='text-sm text-muted-foreground mt-1'>{formatDate(new Date())}</p>
      </div>

      {/* Action Queue Cards */}
      <OperatorStatsCards pendingVerifications={actionCounts?.pendingVerifications || 0} pendingServiceReviews={actionCounts?.pendingServiceReviews || 0} pendingApplications={stats?.pendingApplications || 0} monthlyRevenue={stats?.monthlyRevenue || 0} />

      <OperatorUserStats stats={stats} />

      {/* Alerts Section - only renders if alerts exist */}
      {alerts && alerts.length > 0 && <OperatorAlertsSection alerts={alerts} isLoading={false} />}

      {/* Pending Approvals Table */}
      <PendingApprovalsTable approvals={transformedApprovals} totalCount={pendingVerificationsCount} onApprove={handleApprove} onReject={handleReject} isLoading={pendingVerificationsData === undefined || pendingVerificationsCount === undefined} />

      {/* Analytics Section */}
      <div className='grid lg:grid-cols-2 gap-6'>
        <Suspense fallback={<VizFallback />}>
          <WeeklyActivityChart data={weeklyActivity || []} isLoading={weeklyActivity === undefined} />
        </Suspense>
        <Suspense fallback={<VizFallback />}>
          <RegionalDistributionChart data={regionalDistribution || []} isLoading={regionalDistribution === undefined} />
        </Suspense>
      </div>

      {/* Service Map - Full Width */}
      <Suspense fallback={<VizFallback className='h-96' />}>
        <ServiceMap locations={serviceLocations || []} isLoading={serviceLocations === undefined} />
      </Suspense>
    </div>
  );
};

export const Route = createFileRoute('/_protected/operators/dashboard')({
  component: OperatorDashboard,
});
